import React, { useEffect, useMemo, useState } from 'react';
import FdModal from '../FdModal';
import { useAuth } from '../../context/AuthContext';
import {
  formatCurrency,
  formatDate,
  generateLiquidationNo,
  normalizeSearch,
  parseNumber,
  todayIso,
} from './utils';
import { matchesDateRange, triggerCsvDownload, triggerExcelLikeDownload } from '../../utils/exportUtils';
import TablePagination, { usePagination } from '../TablePagination';

function createDefaultForm() {
  return {
    date: todayIso(),
    voucher_no: '',
    voucher_date: '',
    purpose: '',
    cash_advance_amount: '',
    audited_by: '',
    branch_id: '',
    items: [{ description: '', amount: '' }],
    attachments: [],
  };
}

export default function LiquidationsTab({
  liquidations,
  paymentRequests,
  branches,
  isLoading,
  isSubmitting,
  onCreateLiquidation,
  onUpdateLiquidation,
  onUploadLiquidationAttachment,
  canCreateLiquidation,
  canUpdateLiquidation,
  canUploadAttachment,
}) {
  const { isAdmin, userBranchName } = useAuth();
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingLiquidation, setEditingLiquidation] = useState(null);
  const [selectedPaymentRequestId, setSelectedPaymentRequestId] = useState('');
  const [reviewTarget, setReviewTarget] = useState(null);
  const [declineTarget, setDeclineTarget] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const canApproveLiquidation = isAdmin;

  useEffect(() => {
    if (!isAdmin && userBranchName) {
      setSelectedBranch(userBranchName);
    }
  }, [isAdmin, userBranchName]);
  const [form, setForm] = useState(createDefaultForm);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [attachmentError, setAttachmentError] = useState('');

  const branchOptions = useMemo(() => {
    const names = new Set();
    branches.forEach((branch) => {
      if (branch?.name) {
        names.add(branch.name);
      }
    });
    liquidations.forEach((row) => {
      if (row?.branch) {
        names.add(row.branch);
      }
    });
    return Array.from(names).sort((left, right) => left.localeCompare(right));
  }, [branches, liquidations]);

  // Released Cash Advance AND Petty Cash requests can be liquidated. Petty Cash
  // used to go through replenishment, but that module is temporarily hidden
  // (QA 2026-07), so both request types are liquidated here for now.
  const forLiquidationRequests = useMemo(
    () => paymentRequests.filter((row) => ['Cash Advance', 'Petty Cash'].includes(String(row.request_type || ''))
      && (row.status === 'Released' || row.status === 'For Liquidation')),
    [paymentRequests]
  );

  const selectedPaymentRequest = useMemo(
    () => paymentRequests.find((row) => Number(row.id) === Number(selectedPaymentRequestId)) || null,
    [paymentRequests, selectedPaymentRequestId]
  );

  const filteredRows = useMemo(() => {
    const searchKey = normalizeSearch(search);

    return liquidations
      .filter((row) => selectedBranch === 'all' || String(row.branch || '') === selectedBranch)
      .filter((row) => {
        if (!searchKey) {
          return true;
        }
        const text = [
          row.liquidation_no,
          row.payment_request_no,
          row.branch,
          row.purpose,
          row.status,
          row.voucher_no,
        ]
          .map((value) => normalizeSearch(value))
          .join(' ');
        return text.includes(searchKey);
      })
      .filter((row) => matchesDateRange(row.date, dateFrom, dateTo));
  }, [liquidations, selectedBranch, search, dateFrom, dateTo]);

  const pagination = usePagination(filteredRows);

  const EXPORT_HEADERS = ['Liq. No.', 'Date', 'Branch', 'Linked RFP', 'Purpose', 'Docs', 'Total Spent', 'Cash Advance', 'Excess', 'Status'];

  function buildExportRows() {
    return filteredRows.map((row) => [
      row.liquidation_no || '',
      formatDate(row.date),
      row.branch || '',
      row.payment_request_no || '',
      row.purpose || '',
      Array.isArray(row.attachments) ? row.attachments.length : 0,
      Number(row.total_spent || 0).toFixed(2),
      Number(row.cash_advance_amount || 0).toFixed(2),
      Number(row.excess || 0).toFixed(2),
      row.status || 'Draft',
    ]);
  }

  function exportCsv() {
    triggerCsvDownload(`liquidations-${todayIso()}.csv`, EXPORT_HEADERS, buildExportRows());
  }

  function exportExcel() {
    triggerExcelLikeDownload(`liquidations-${todayIso()}.xls`, EXPORT_HEADERS, buildExportRows());
  }

  const totalSpent = useMemo(
    () => form.items.reduce((sum, item) => sum + parseNumber(item.amount, 0), 0),
    [form.items]
  );

  const cashAdvanceAmount = useMemo(
    () => parseNumber(form.cash_advance_amount, 0),
    [form.cash_advance_amount]
  );

  const excess = useMemo(
    () => cashAdvanceAmount - totalSpent,
    [cashAdvanceAmount, totalSpent]
  );

  function addExpenseItem() {
    setForm((previous) => ({
      ...previous,
      items: [...previous.items, { description: '', amount: '' }],
    }));
  }

  function removeExpenseItem(index) {
    setForm((previous) => {
      if (previous.items.length <= 1) {
        return {
          ...previous,
          items: [{ description: '', amount: '' }],
        };
      }

      return {
        ...previous,
        items: previous.items.filter((_, itemIndex) => itemIndex !== index),
      };
    });
  }

  function updateExpenseItem(index, field, value) {
    setForm((previous) => ({
      ...previous,
      items: previous.items.map((item, itemIndex) => (
        itemIndex === index
          ? { ...item, [field]: value }
          : item
      )),
    }));
  }

  // Upload immediately when a file is picked so it is always captured into
  // form.attachments before the user saves (a separate Upload click was easy to miss).
  async function uploadAttachment(file) {
    if (!canUploadAttachment) {
      setAttachmentError('You do not have permission to upload attachments.');
      return;
    }

    if (!file || typeof onUploadLiquidationAttachment !== 'function') {
      return;
    }

    setAttachmentError('');
    setIsUploadingAttachment(true);

    try {
      const response = await onUploadLiquidationAttachment(file);
      const attachment = response?.data?.attachment || response?.attachment || null;
      if (!attachment || !attachment.url) {
        setAttachmentError('Upload completed but no attachment URL was returned.');
        return;
      }

      setForm((previous) => ({
        ...previous,
        attachments: [
          ...previous.attachments,
          {
            name: attachment.name || file.name || null,
            url: attachment.url || null,
          },
        ],
      }));
    } catch (error) {
      setAttachmentError(error?.message || 'Failed to upload attachment.');
    } finally {
      setIsUploadingAttachment(false);
    }
  }

  function removeAttachment(index) {
    setForm((previous) => ({
      ...previous,
      attachments: previous.attachments.filter((_, attachmentIndex) => attachmentIndex !== index),
    }));
  }

  function handleSelectPaymentRequest(paymentRequestId) {
    setSelectedPaymentRequestId(paymentRequestId);

    const paymentRequest = paymentRequests.find((row) => Number(row.id) === Number(paymentRequestId)) || null;
    if (!paymentRequest) {
      return;
    }

    setForm((previous) => ({
      ...previous,
      branch_id: paymentRequest.branch_id || previous.branch_id || '',
      cash_advance_amount: paymentRequest.total_amount ?? '',
      purpose: Array.isArray(paymentRequest.particulars)
        ? paymentRequest.particulars
          .map((item) => String(item.description || '').trim())
          .filter(Boolean)
          .join(', ')
        : previous.purpose,
      voucher_no: paymentRequest.transaction_no || previous.voucher_no,
    }));
  }

  async function submitCreate() {
    if (!canCreateLiquidation) {
      return;
    }

    // The file attachment is required before a liquidation can be saved.
    if (form.attachments.length === 0) {
      setAttachmentError('At least one file attachment is required.');
      return;
    }

    const branchId = selectedPaymentRequest?.branch_id || (form.branch_id ? Number(form.branch_id) : null);
    const branch = branches.find((item) => Number(item.id) === Number(branchId))
      || branches.find((item) => String(item.name || '') === String(selectedPaymentRequest?.branch || ''))
      || null;
    const branchName = branch?.name || selectedPaymentRequest?.branch || '';

    const normalizedItems = form.items
      .map((item) => ({
        description: String(item.description || '').trim(),
        amount: parseNumber(item.amount, 0),
      }))
      .filter((item) => item.description || item.amount > 0);

    const payload = {
      liquidation_no: generateLiquidationNo(branchName, liquidations),
      date: form.date || null,
      branch_id: branchId || null,
      branch: branchName || null,
      payment_request_id: selectedPaymentRequest?.id || null,
      payment_request_no: selectedPaymentRequest?.transaction_no || null,
      voucher_no: String(form.voucher_no || '').trim() || null,
      voucher_date: form.voucher_date || null,
      purpose: String(form.purpose || '').trim() || null,
      items: normalizedItems,
      total_spent: normalizedItems.reduce((sum, item) => sum + item.amount, 0),
      cash_advance_amount: cashAdvanceAmount,
      excess,
      audited_by: String(form.audited_by || '').trim() || null,
      attachments: form.attachments,
      status: 'Draft',
    };

    const success = await onCreateLiquidation(payload);
    if (success) {
      setIsCreateOpen(false);
      setSelectedPaymentRequestId('');
      setForm(createDefaultForm());
      setAttachmentError('');
    }
  }

  // A declined liquidation can be corrected by its requester and saved before
  // being reprocessed back into the approval workflow.
  function openEditForm(row) {
    if (!canUpdateLiquidation) {
      return;
    }

    setEditingLiquidation(row);
    setSelectedPaymentRequestId('');
    setForm({
      date: row.date || todayIso(),
      voucher_no: row.voucher_no || '',
      voucher_date: row.voucher_date || '',
      purpose: row.purpose || '',
      cash_advance_amount: row.cash_advance_amount ?? '',
      audited_by: row.audited_by || '',
      branch_id: row.branch_id != null ? String(row.branch_id) : '',
      items: Array.isArray(row.items) && row.items.length > 0
        ? row.items.map((item) => ({
          description: item.description || '',
          amount: item.amount ?? '',
        }))
        : [{ description: '', amount: '' }],
      attachments: Array.isArray(row.attachments)
        ? row.attachments.map((attachment) => ({
          name: attachment.name || null,
          url: attachment.url || null,
        }))
        : [],
    });
    setAttachmentError('');
    setIsCreateOpen(true);
  }

  function closeFormModal() {
    setIsCreateOpen(false);
    setEditingLiquidation(null);
    setSelectedPaymentRequestId('');
    setForm(createDefaultForm());
    setAttachmentError('');
  }

  async function submitUpdate() {
    if (!canUpdateLiquidation || !editingLiquidation) {
      return;
    }

    // The file attachment stays required when editing a declined liquidation.
    if (form.attachments.length === 0) {
      setAttachmentError('At least one file attachment is required.');
      return;
    }

    const normalizedItems = form.items
      .map((item) => ({
        description: String(item.description || '').trim(),
        amount: parseNumber(item.amount, 0),
      }))
      .filter((item) => item.description || item.amount > 0);

    const payload = {
      date: form.date || null,
      voucher_no: String(form.voucher_no || '').trim() || null,
      voucher_date: form.voucher_date || null,
      purpose: String(form.purpose || '').trim() || null,
      items: normalizedItems,
      total_spent: normalizedItems.reduce((sum, item) => sum + item.amount, 0),
      cash_advance_amount: cashAdvanceAmount,
      excess,
      audited_by: String(form.audited_by || '').trim() || null,
      attachments: form.attachments,
    };

    const success = await onUpdateLiquidation(editingLiquidation.id, payload);
    if (success) {
      closeFormModal();
    }
  }

  async function submitSave() {
    if (editingLiquidation) {
      await submitUpdate();
    } else {
      await submitCreate();
    }
  }

  async function quickUpdateStatus(row, status) {
    if (!canUpdateLiquidation) {
      return;
    }

    await onUpdateLiquidation(row.id, { status });
  }

  async function approveFromReview() {
    if (!canApproveLiquidation || !reviewTarget) {
      return;
    }

    const success = await onUpdateLiquidation(reviewTarget.id, { status: 'Approved' });
    if (success) {
      setReviewTarget(null);
    }
  }

  async function submitDecline() {
    if (!canApproveLiquidation || !declineTarget) {
      return;
    }

    const reason = String(declineReason || '').trim();
    if (!reason) {
      return;
    }

    const success = await onUpdateLiquidation(declineTarget.id, {
      status: 'Declined',
      decline_reason: reason,
    });
    if (success) {
      setDeclineTarget(null);
      setDeclineReason('');
      setReviewTarget(null);
    }
  }

  async function reprocess(row) {
    // The submitting user re-submits a declined liquidation for another review.
    await onUpdateLiquidation(row.id, { status: 'Submitted' });
  }

  return (
    <div className="finance-tab-content">
      <div className="finance-toolbar">
        <div className="finance-filter-group">
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search liquidation no, linked request, purpose..."
          />
          <select value={selectedBranch} onChange={(event) => setSelectedBranch(event.target.value)} disabled={!isAdmin}>
            {isAdmin ? <option value="all">All Branches</option> : null}
            {branchOptions.map((branchName) => (
              <option key={branchName} value={branchName}>
                {branchName}
              </option>
            ))}
          </select>
          <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} title="Date from" />
          <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} title="Date to" />
        </div>
        <div className="dashboard-actions">
          <button type="button" className="profile-secondary-btn" onClick={exportCsv}>
            Export CSV
          </button>
          <button type="button" className="profile-secondary-btn" onClick={exportExcel}>
            Export Excel
          </button>
          {canCreateLiquidation ? (
            <button
              type="button"
              className="profile-secondary-btn"
              onClick={() => {
                setEditingLiquidation(null);
                setSelectedPaymentRequestId('');
                setForm(createDefaultForm());
                setAttachmentError('');
                setIsCreateOpen(true);
              }}
            >
              + New Liquidation
            </button>
          ) : null}
        </div>
      </div>

      {isLoading ? (
        <div className="dashboard-loading">Loading liquidations...</div>
      ) : filteredRows.length === 0 ? (
        <div className="dashboard-empty">No liquidations found.</div>
      ) : (
        <div className="dashboard-table-wrap">
          <table className="dashboard-table finance-table">
            <thead>
              <tr>
                <th>Liq. No.</th>
                <th>Date</th>
                <th>Branch</th>
                <th>Linked RFP</th>
                <th>Purpose</th>
                <th>Docs</th>
                <th>Total Spent</th>
                <th>Cash Advance</th>
                <th>Excess</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagination.pagedItems.map((row) => (
                <tr key={row.id}>
                  <td className="finance-mono-cell">{row.liquidation_no || '-'}</td>
                  <td>{formatDate(row.date)}</td>
                  <td>{row.branch || '-'}</td>
                  <td className="finance-mono-cell">{row.payment_request_no || '-'}</td>
                  <td>{row.purpose || '-'}</td>
                  <td>{Array.isArray(row.attachments) ? row.attachments.length : 0}</td>
                  <td className="finance-number-cell">{formatCurrency(row.total_spent)}</td>
                  <td className="finance-number-cell">{formatCurrency(row.cash_advance_amount)}</td>
                  <td className="finance-number-cell">{formatCurrency(row.excess)}</td>
                  <td>
                    <span className={`status-badge ${
                      row.status === 'Approved'
                        ? 'status-active'
                        : row.status === 'Declined'
                          ? 'status-cancelled'
                          : 'status-inactive'
                    }`}>
                      {row.status || 'Draft'}
                    </span>
                    {row.status === 'Declined' && row.decline_reason ? (
                      <div className="finance-decline-reason" title={row.decline_reason}>
                        Reason: {row.decline_reason}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <div className="dashboard-actions">
                      <button
                        type="button"
                        className="profile-secondary-btn profile-secondary-btn-xs"
                        onClick={() => setReviewTarget(row)}
                      >
                        View
                      </button>

                      {row.status === 'Draft' && canUpdateLiquidation ? (
                        <button
                          type="button"
                          className="profile-secondary-btn profile-secondary-btn-xs"
                          onClick={() => quickUpdateStatus(row, 'Submitted')}
                          disabled={isSubmitting}
                        >
                          Submit
                        </button>
                      ) : null}

                      {row.status === 'Submitted' && canApproveLiquidation ? (
                        <button
                          type="button"
                          className="profile-secondary-btn profile-secondary-btn-xs"
                          onClick={() => setReviewTarget(row)}
                          disabled={isSubmitting}
                        >
                          Review
                        </button>
                      ) : null}

                      {row.status === 'Declined' && canUpdateLiquidation ? (
                        <>
                          <button
                            type="button"
                            className="profile-secondary-btn profile-secondary-btn-xs"
                            onClick={() => openEditForm(row)}
                            disabled={isSubmitting}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="profile-secondary-btn profile-secondary-btn-xs"
                            onClick={() => reprocess(row)}
                            disabled={isSubmitting}
                          >
                            Reprocess
                          </button>
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading ? <TablePagination {...pagination} /> : null}

      <FdModal
        isOpen={isCreateOpen}
        onClose={closeFormModal}
        wide
        eyebrow="Finance"
        title={editingLiquidation ? `Edit Liquidation ${editingLiquidation.liquidation_no || ''}` : 'New Liquidation'}
        subtitle={editingLiquidation
          ? 'Update the declined liquidation, save, then reprocess it for another review.'
          : 'Create liquidation report from a released Cash Advance or Petty Cash payment request.'}
        footer={(
          <>
            <button
              type="button"
              className="fd-btn fd-btn-cancel"
              onClick={closeFormModal}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="fd-btn fd-btn-primary"
              onClick={submitSave}
              disabled={isSubmitting
                || isUploadingAttachment
                || (editingLiquidation ? !canUpdateLiquidation : (!canCreateLiquidation || !selectedPaymentRequestId))}
            >
              {isSubmitting ? 'Saving...' : (editingLiquidation ? 'Save Changes' : 'Save Liquidation')}
            </button>
          </>
        )}
      >
        <div className="finance-modal-grid">
          {editingLiquidation ? (
            <>
              {editingLiquidation.decline_reason ? (
                <div className="fd-field fd-field-wide">
                  <div className="error-box">
                    Declined: {editingLiquidation.decline_reason}
                  </div>
                </div>
              ) : null}
              <div className="finance-link-preview">
                <p>{editingLiquidation.liquidation_no || '-'}</p>
                <span>Linked RFP: {editingLiquidation.payment_request_no || '-'}</span>
                <strong>{formatCurrency(editingLiquidation.cash_advance_amount)}</strong>
              </div>
            </>
          ) : (
            <div className="fd-field fd-field-wide">
              <label>Link Payment Request (Cash Advance / Petty Cash)</label>
              <select value={selectedPaymentRequestId} onChange={(event) => handleSelectPaymentRequest(event.target.value)}>
                <option value="">Select payment request</option>
                {forLiquidationRequests.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.transaction_no} - {row.requestor_name} - {row.request_type} ({formatCurrency(row.total_amount)})
                  </option>
                ))}
              </select>
            </div>
          )}

          {!editingLiquidation && selectedPaymentRequest ? (
            <div className="finance-link-preview">
              <p>{selectedPaymentRequest.transaction_no}</p>
              <span>{selectedPaymentRequest.requestor_name} - {selectedPaymentRequest.request_type}</span>
              <strong>{formatCurrency(selectedPaymentRequest.total_amount)}</strong>
            </div>
          ) : null}

          <div className="fd-field">
            <label>Date</label>
            <input
              type="date"
              value={form.date}
              onChange={(event) => setForm((previous) => ({ ...previous, date: event.target.value }))}
            />
          </div>
          <div className="fd-field">
            <label>Voucher No.</label>
            <input
              type="text"
              value={form.voucher_no}
              onChange={(event) => setForm((previous) => ({ ...previous, voucher_no: event.target.value }))}
            />
          </div>
          <div className="fd-field">
            <label>Voucher Date</label>
            <input
              type="date"
              value={form.voucher_date}
              onChange={(event) => setForm((previous) => ({ ...previous, voucher_date: event.target.value }))}
            />
          </div>
          <div className="fd-field">
            <label>Cash Advance Amount</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.cash_advance_amount}
              onChange={(event) => setForm((previous) => ({ ...previous, cash_advance_amount: event.target.value }))}
            />
          </div>
          <div className="fd-field fd-field-wide">
            <label>Purpose</label>
            <input
              type="text"
              value={form.purpose}
              onChange={(event) => setForm((previous) => ({ ...previous, purpose: event.target.value }))}
              placeholder="Purpose of cash advance"
            />
          </div>

          <div className="fd-field fd-field-wide">
            <label>Expense Items</label>
            <div className="finance-line-items">
              {form.items.map((item, index) => (
                <div key={`expense-${index}`} className="finance-line-item-row">
                  <input
                    type="text"
                    value={item.description}
                    onChange={(event) => updateExpenseItem(index, 'description', event.target.value)}
                    placeholder="Description"
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.amount}
                    onChange={(event) => updateExpenseItem(index, 'amount', event.target.value)}
                    placeholder="Amount"
                  />
                  <button
                    type="button"
                    className="profile-danger-btn profile-danger-btn-xs"
                    onClick={() => removeExpenseItem(index)}
                    disabled={form.items.length <= 1}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <div className="finance-line-actions">
              <button type="button" className="profile-secondary-btn profile-secondary-btn-xs" onClick={addExpenseItem}>
                + Add Item
              </button>
              <span className="finance-line-total">Total Spent: {formatCurrency(totalSpent)}</span>
            </div>
          </div>

          <div className="fd-field fd-field-wide">
            <label>Attachments *</label>
            <div className="finance-attachment-row">
              <input
                type="file"
                onChange={(event) => {
                  const file = event.target.files?.[0] || null;
                  event.target.value = '';
                  if (file) {
                    uploadAttachment(file);
                  }
                }}
                accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt"
                disabled={!canUploadAttachment || isUploadingAttachment || isSubmitting}
              />
              {isUploadingAttachment ? <span className="finance-inline-note">Uploading...</span> : null}
            </div>
            {form.attachments.length === 0 && !isUploadingAttachment ? (
              <span className="finance-inline-note">At least one file attachment is required.</span>
            ) : null}
            {attachmentError ? (
              <div className="error-box" style={{ marginTop: 8 }}>{attachmentError}</div>
            ) : null}
            {form.attachments.length > 0 ? (
              <div className="finance-attachment-list">
                {form.attachments.map((attachment, index) => (
                  <div key={`attachment-${index}`} className="finance-attachment-item">
                    <div>
                      <p>{attachment.name || 'Attachment'}</p>
                      {attachment.url ? (
                        <a href={attachment.url} target="_blank" rel="noreferrer">{attachment.url}</a>
                      ) : (
                        <span>-</span>
                      )}
                    </div>
                    <button type="button" className="profile-danger-btn profile-danger-btn-xs" onClick={() => removeAttachment(index)}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <div className="fd-field">
            <label>Audited By</label>
            <input
              type="text"
              value={form.audited_by}
              onChange={(event) => setForm((previous) => ({ ...previous, audited_by: event.target.value }))}
              placeholder="Auditor name"
            />
          </div>
          <div className="finance-summary-box">
            <p>Total Spent: <strong>{formatCurrency(totalSpent)}</strong></p>
            <p>Cash Advance: <strong>{formatCurrency(cashAdvanceAmount)}</strong></p>
            <p>Excess: <strong>{formatCurrency(excess)}</strong></p>
          </div>
        </div>
      </FdModal>

      <FdModal
        isOpen={Boolean(reviewTarget)}
        onClose={() => setReviewTarget(null)}
        wide
        eyebrow="Finance"
        title="Liquidation Details"
        subtitle="Review the liquidation, expense items, and attachments."
        footer={(
          <>
            <button type="button" className="fd-btn fd-btn-cancel" onClick={() => setReviewTarget(null)} disabled={isSubmitting}>
              Close
            </button>
            {canApproveLiquidation && reviewTarget?.status === 'Submitted' ? (
              <>
                <button
                  type="button"
                  className="profile-danger-btn"
                  onClick={() => {
                    setDeclineTarget(reviewTarget);
                    setDeclineReason('');
                  }}
                  disabled={isSubmitting}
                >
                  Decline
                </button>
                <button
                  type="button"
                  className="fd-btn fd-btn-primary"
                  onClick={approveFromReview}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Approve'}
                </button>
              </>
            ) : null}
          </>
        )}
      >
        {reviewTarget ? (
          <div className="finance-modal-grid">
            <div className="fd-field"><label>Liquidation No.</label><input value={reviewTarget.liquidation_no || '-'} readOnly /></div>
            <div className="fd-field"><label>Date</label><input value={formatDate(reviewTarget.date) || '-'} readOnly /></div>
            <div className="fd-field"><label>Linked RFP</label><input value={reviewTarget.payment_request_no || '-'} readOnly /></div>
            <div className="fd-field"><label>Status</label><input value={reviewTarget.status || 'Draft'} readOnly /></div>
            <div className="fd-field"><label>Branch</label><input value={reviewTarget.branch || '-'} readOnly /></div>
            <div className="fd-field"><label>Voucher No.</label><input value={reviewTarget.voucher_no || '-'} readOnly /></div>
            <div className="fd-field fd-field-wide"><label>Purpose</label><input value={reviewTarget.purpose || '-'} readOnly /></div>
            <div className="fd-field"><label>Total Spent</label><input value={formatCurrency(reviewTarget.total_spent)} readOnly /></div>
            <div className="fd-field"><label>Cash Advance</label><input value={formatCurrency(reviewTarget.cash_advance_amount)} readOnly /></div>
            <div className="fd-field"><label>Excess</label><input value={formatCurrency(reviewTarget.excess)} readOnly /></div>

            {reviewTarget.status === 'Declined' && reviewTarget.decline_reason ? (
              <div className="fd-field fd-field-wide">
                <label>Decline Reason</label>
                <textarea className="collections-textarea" value={reviewTarget.decline_reason} readOnly />
              </div>
            ) : null}

            <div className="fd-field fd-field-wide">
              <label>Expense Items</label>
              {Array.isArray(reviewTarget.items) && reviewTarget.items.length > 0 ? (
                <div className="finance-attachment-list">
                  {reviewTarget.items.map((item, index) => (
                    <div key={item.id || `item-${index}`} className="finance-select-row">
                      <span>{item.description || '-'}</span>
                      <strong>{formatCurrency(item.amount)}</strong>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="dashboard-empty">No expense items recorded.</span>
              )}
            </div>

            <div className="fd-field fd-field-wide">
              <label>Attachments</label>
              {Array.isArray(reviewTarget.attachments) && reviewTarget.attachments.length > 0 ? (
                <div className="finance-attachment-list">
                  {reviewTarget.attachments.map((attachment) => (
                    <div key={attachment.id || attachment.url} className="finance-attachment-item">
                      <a href={attachment.url} target="_blank" rel="noreferrer">
                        {attachment.name || 'Attachment'}
                      </a>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="dashboard-empty">No attachments uploaded for this liquidation.</span>
              )}
            </div>
          </div>
        ) : null}
      </FdModal>

      <FdModal
        isOpen={Boolean(declineTarget)}
        onClose={() => { setDeclineTarget(null); setDeclineReason(''); }}
        eyebrow="Finance"
        title="Decline Liquidation"
        subtitle="Provide a reason. The requestor will see this and may reprocess."
        footer={(
          <>
            <button type="button" className="fd-btn fd-btn-cancel" onClick={() => { setDeclineTarget(null); setDeclineReason(''); }} disabled={isSubmitting}>
              Cancel
            </button>
            <button
              type="button"
              className="profile-danger-btn"
              onClick={submitDecline}
              disabled={isSubmitting || !declineReason.trim()}
            >
              {isSubmitting ? 'Saving...' : 'Confirm Decline'}
            </button>
          </>
        )}
      >
        <div className="finance-modal-grid">
          <div className="finance-link-preview">
            <p>{declineTarget?.liquidation_no || '-'}</p>
            <span>{declineTarget?.payment_request_no || '-'}</span>
            <strong>{formatCurrency(declineTarget?.total_spent)}</strong>
          </div>
          <div className="fd-field fd-field-wide">
            <label>Decline Reason *</label>
            <textarea
              className="collections-textarea"
              value={declineReason}
              onChange={(event) => setDeclineReason(event.target.value)}
              placeholder="Explain why this liquidation is being declined"
            />
          </div>
        </div>
      </FdModal>
    </div>
  );
}
